import React, { useEffect, useState } from 'react';
import { Modal, Form, Input } from 'antd';
import QuillRichText from 'components/QuillRichText';
import s from './s.module.less';
import commonS from 'styles/common.module.less';
import { TContentMgmt } from '../../../type';
import { ServerMeta } from 'types/common';

interface ContentMgmtModalProps {
    link?: string;
    visible: boolean;
    onCancel: () => void;
    onSave: (data: TContentMgmt) => void;
    initialData?: TContentMgmt;
    condition?: ServerMeta;
}

const ContentMgmtModal: React.FC<ContentMgmtModalProps> = ({
    link,
    visible,
    onCancel,
    onSave,
    initialData,
    condition,
}) => {
    const [form] = Form.useForm<TContentMgmt>();
    const [charCounts, setCharCounts] = useState({
        title: 0,
        summary: 0,
        medicalDisclaimerContent: 0,
        mainContent: 0,
    });

    // 更新字符计数
    const updateCharCounts = (data: Partial<TContentMgmt>) => {
        setCharCounts({
            title: (data.title || '').length,
            summary: (data.summary || '').replace(/<[^>]*>/g, '').length,
            medicalDisclaimerContent: (data.medicalDisclaimerContent || '').replace(/<[^>]*>/g, '').length,
            mainContent: (data.mainContent || '').replace(/<[^>]*>/g, '').length,
        });
    };

    // 当模态框打开或初始数据变化时，设置表单数据
    useEffect(() => {
        if (visible) {
            if (initialData) {
                form.setFieldsValue(initialData);
                updateCharCounts(initialData);
            } else {
                // 设置默认值
                const defaultValues = {
                    url: '',
                    title: '',
                    summary: '',
                    medicalDisclaimerContent: '',
                    mainContent: '',
                };
                form.setFieldsValue(defaultValues);
                setCharCounts({
                    title: 0,
                    summary: 0,
                    medicalDisclaimerContent: 0,
                    mainContent: 0,
                });
            }
        }
    }, [visible, initialData, form]);

    // 处理表单提交
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            onSave(values);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    // 处理取消
    const handleCancel = () => {
        form.resetFields();
        onCancel();
    };

    // 处理字段值变化
    const handleFieldChange = (fieldName: keyof TContentMgmt, value: string) => {
        form.setFieldValue(fieldName, value);

        // 更新字符计数
        const newCounts = { ...charCounts };
        if (fieldName === 'title') {
            newCounts.title = value.length;
        } else if (fieldName === 'summary') {
            newCounts.summary = value.replace(/<[^>]*>/g, '').length;
        } else if (fieldName === 'medicalDisclaimerContent') {
            newCounts.medicalDisclaimerContent = value.replace(/<[^>]*>/g, '').length;
        } else if (fieldName === 'mainContent') {
            newCounts.mainContent = value.replace(/<[^>]*>/g, '').length;
        }
        setCharCounts(newCounts);
    };

    return (
        <Modal
            title={`Edit ${condition?.content}`}
            className={commonS.modalFixHeightWrap}
            open={visible}
            onCancel={handleCancel}
            okText="Save changes"
            onOk={handleSubmit}
            width={543}
            destroyOnClose
        >
            <Form
                form={form}
                layout="vertical"
                className={s.form}
            >
                {/* URL Field */}
                <Form.Item
                    label="URL"
                >
                    <Input
                        value={link}
                        disabled
                        // placeholder="https://www.helloklarity.com/medication/abnormal periods"
                    />
                </Form.Item>

                {/* Title Field */}
                <Form.Item
                    label="Title"
                    name="title"
                    rules={[
                        { required: true, message: 'Please enter title' },
                        { max: 70, message: 'Title cannot exceed 70 characters' },
                    ]}
                >
                    <Input
                        placeholder="Top Abnormal periods medications: What you need to know"
                        maxLength={70}
                        showCount
                        onChange={(e) => handleFieldChange('title', e.target.value)}
                    />
                </Form.Item>

                {/* Quick Answer Content Field */}
                <Form.Item
                    label="Summary *"
                    name="summary"
                    rules={[
                        { required: true, message: 'Please enter summary' },
                        {
                            validator: (_, value) => {
                                const plainText = value?.replace(/<[^>]*>/g, '') || '';
                                if (plainText.length > 8000) {
                                    return Promise.reject(new Error('Content cannot exceed 8000 characters'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <QuillRichText
                        keepHtmlLabels
                        height="auto"
                        value={form.getFieldValue('summary')}
                        // plcaeholder="FDA-approved anxiety medications include SSRIs (sertraline, paroxetine, escitalopram), SNRIs (venlafaxine, duloxetine), and benzodiazepines (lorazepam, alprazolam). These medications balance brain chemicals and typically require 4-6 weeks to achieve full effectiveness under medical supervision."
                        onChange={(value) => handleFieldChange('summary', value)}
                        showCount
                        maxLength={8000}
                    />
                </Form.Item>

                {/* Medical Disclaimer Content Field */}
                <Form.Item
                    label="Medical disclaimer content *"
                    name="medicalDisclaimerContent"
                    rules={[
                        { required: true, message: 'Please enter medical disclaimer content' },
                        {
                            validator: (_, value) => {
                                const plainText = value?.replace(/<[^>]*>/g, '') || '';
                                if (plainText.length > 8000) {
                                    return Promise.reject(new Error('Content cannot exceed 8000 characters'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <QuillRichText
                        keepHtmlLabels
                        height="auto"
                        value={form.getFieldValue('medicalDisclaimerContent')}
                        // plcaeholder="This information is for educational purposes only and is not intended as medical advice. Always consult with a qualified healthcare professional before starting, stopping, or changing any medication."
                        onChange={(value) => handleFieldChange('medicalDisclaimerContent', value)}
                        showCount
                        maxLength={8000}
                    />
                </Form.Item>

                {/* Main Content Field */}
                <Form.Item
                    label="Main content *"
                    name="mainContent"
                    rules={[
                        { required: true, message: 'Please enter main content' },
                        {
                            validator: (_, value) => {
                                const plainText = value?.replace(/<[^>]*>/g, '') || '';
                                if (plainText.length > 8000) {
                                    return Promise.reject(new Error('Content cannot exceed 8000 characters'));
                                }
                                return Promise.resolve();
                            },
                        },
                    ]}
                >
                    <QuillRichText
                        keepHtmlLabels
                        height="370px"
                        value={form.getFieldValue('mainContent')}
                        // plcaeholder="What medications are commonly used for ADHD?
                        // There are two main types of ADHD medications: stimulants and non-stimulants.
                        // Stimulants (e.g., Adderall, Ritalin) are often first-line treatments that work by increasing dopamine and norepinephrine in the brain, which can improve attention and reduce impulsivity.
                        // Non-stimulants (e.g., Strattera, Intuniv) may be prescribed if stimulants are not effective or cause unwanted side effects. These medications affect different brain chemicals and may take longer to show effects."
                        onChange={(value) => handleFieldChange('mainContent', value)}
                        showCount
                        maxLength={8000}
                    />
                </Form.Item>
            </Form>
        </Modal>
    );
};

export default ContentMgmtModal;
